/**
 * Drives the open document's version chain (DESIGN UD-27, `docs/plan/VERSION-CHAIN-DESIGN.md`): loads it when a document
 * opens, gives every action the latest version's text, adds each finished step as the next version, switches the views to
 * a version's full analysis (made in a worker of its own, `version-client.ts`), and runs Undo, Start again, the latest
 * step's reverts and "Continue from this result". The upload's own view model is kept here while the chain holds a version,
 * so the original is one click away; the latest version's view model is kept too, so going back to it is instant.
 */
import { effect, signal } from '@preact/signals';
import type { MarkdownEdit, ReviewItem } from '../../shared/md-review.ts';
import type { VersionTag } from '../../shared/protocol.ts';
import type { ViewModel } from '../../shared/viewmodel.ts';
import { analysisPriorMs, recordAnalysisRate } from '../rates.ts';
import { addNotice, isBootRestored, result } from '../state.ts';
import { VERSIONS } from '../strings-bench.ts';
import { beginWork, endWork, updateWork } from '../work.ts';
import { client as analysisClient } from '../worker-client.ts';
import {
	type DocChain,
	type VersionMaker,
	type VersionRef,
	adopt,
	append,
	canAdopt,
	canAppend,
	editsForHash,
	emptyChain,
	latestRef,
	reviseLatest,
	setMarkdownEdits,
	textHash,
	undo,
} from './chain.ts';
import type { BenchJob } from './job.ts';
import { faultReason } from './messages.ts';
import {
	chain,
	jobs,
	liveJobId,
	rewrittenText,
	versionError,
	versionWork,
	versionsAsk,
	versionsAskOpen,
	viewing,
} from './state.ts';
import { getChain, putChain, removeChain } from './store.ts';
import { analyseVersionInWorker } from './version-client.ts';

/** The document behind a view model: version 0's hash, for an upload and for every version of it. */
export const rootOf = (vm: Pick<ViewModel, 'meta'>): string =>
	vm.meta.version?.root ?? vm.meta.docHash;

/** The upload's view model (version 0), and the latest version's once analysed. */
let originVm: ViewModel | null = null;
let latestVm: { hash: string; vm: ViewModel } | null = null;
/** The hash of the version text whose analysis is on screen (version 0's is the chain's root). */
let shownHash: string | null = null;
/** The chain of the open document finishes loading. */
let loading: Promise<void> = Promise.resolve();
let measuring: AbortController | null = null;
let reviseTimer: ReturnType<typeof setTimeout> | null = null;

/** The upload's view model, or null before a document is open. */
export const origin = (): ViewModel | null => originVm;

/** Waits for the open document's chain (an action right after a document opens). */
export const chainReady = (): Promise<void> => loading;

/** The latest version: its number and hash, its text, its word count. Version 0 is the upload's analysed text. */
export function latest(): { ref: VersionRef; text: string; words: number } | null {
	const c = chain.peek();
	const o = originVm;
	if (!o) return null;
	const v = c?.versions.at(-1);
	if (!c || !v) return { ref: { n: 0, hash: rootOf(o) }, text: o.text, words: o.meta.words };
	return { ref: latestRef(c), text: v.text, words: v.words };
}

/**
 * Whether the screen shows the latest version's own analysis (paragraph numbers on screen are the latest version's). The
 * analysis must be of the latest text itself: after a revert in the latest step, the analysis made before it is not.
 */
export function latestOnScreen(): boolean {
	const c = chain.peek();
	const vm = result.peek();
	if (!c || !vm) return false;
	const n = c.versions.length;
	return (vm.meta.version?.n ?? 0) === n && viewing.peek() === n && shownHash === latestRef(c).hash;
}

/** The text of version `n` (0 is the upload's analysed text), or null. */
export function versionText(n: number): string | null {
	if (n === 0) return originVm?.text ?? null;
	return chain.peek()?.versions[n - 1]?.text ?? null;
}

async function save(next: DocChain): Promise<void> {
	chain.value = next;
	rewrittenText.value =
		versionsAsk.peek() === next.root ? null : (next.versions.at(-1)?.text ?? null);
	await putChain(next);
}

/** Tells the analysis worker which text the reader's find searches (the view model on screen). */
function hold(vm: ViewModel): void {
	analysisClient.send({
		type: 'hold',
		id: analysisClient.id(),
		docHash: vm.meta.docHash,
		text: vm.text,
	});
}

function put(vm: ViewModel, hash: string): void {
	shownHash = hash;
	result.value = vm;
	hold(vm);
}

function stopMeasuring(): void {
	measuring?.abort();
	measuring = null;
	versionWork.value = null;
	endWork('version');
}

/**
 * Shows version `n`: the original at once, the latest at once when its analysis is kept, any other after its full analysis
 * (with progress; the views keep the version they show until it is ready). The next action is unaffected: it always works on
 * the latest version.
 */
export async function showVersion(n: number): Promise<void> {
	const c = chain.peek();
	const o = originVm;
	if (!c || !o) return;
	stopMeasuring();
	versionError.value = null;
	viewing.value = n;
	if (n === 0) {
		put(o, c.root);
		return;
	}
	const v = c.versions[n - 1];
	if (!v) return;
	if (latestVm && latestVm.hash === v.hash) {
		put(latestVm.vm, v.hash);
		return;
	}
	const controller = new AbortController();
	measuring = controller;
	const tag: VersionTag = { root: c.root, n, name: o.meta.name, sourceKind: o.meta.sourceKind };
	const prior = analysisPriorMs(v.words);
	versionWork.value = { n, fraction: 0 };
	beginWork('version', VERSIONS.analysing(String(n)), { priorMs: prior, fraction: 0 });
	const t0 = performance.now();
	try {
		const vm = await analyseVersionInWorker(
			v.text,
			tag,
			(fraction) => {
				if (measuring !== controller) return;
				versionWork.value = { n, fraction };
				updateWork('version', { fraction });
			},
			controller.signal
		);
		if (measuring !== controller) return;
		recordAnalysisRate(vm.meta.words, performance.now() - t0);
		const now = chain.peek();
		// the chain may have moved on while the analysis ran (an Undo, a revert): only a current version is shown
		if (!now || now.root !== c.root || now.versions[n - 1]?.hash !== v.hash) return;
		if (n === now.versions.length) latestVm = { hash: v.hash, vm };
		if (viewing.peek() === n) put(vm, v.hash);
	} catch (e) {
		if (controller.signal.aborted) return;
		versionError.value = VERSIONS.failed(String(n), faultReason(e));
	} finally {
		if (measuring === controller) {
			measuring = null;
			versionWork.value = null;
			endWork('version');
		}
	}
}

/** The Markdown edits stored for the version whose text is `text` (UD-31). */
export const editsOfText = (text: string): MarkdownEdit[] =>
	editsForHash(chain.peek(), textHash(text));

/**
 * What is known of a version's inline Markdown, by the version's text hash: how many spans moved onto new words and how many
 * were dropped, as of the last download or review (the version bar's badge). Nothing is computed ahead of time.
 */
export const markupHints = signal<Readonly<Record<string, { moved: number; dropped: number }>>>({});

/** Records what is known of the Markdown of the version whose text is `text` (the version bar's badge). */
export function setMarkupHint(text: string, moved: number, dropped: number): void {
	markupHints.value = { ...markupHints.peek(), [textHash(text)]: { moved, dropped } };
}

/** The request body both Markdown messages share: version 0's reading text and blocks, and the version's text. */
function markdownRequest(text: string, edits: MarkdownEdit[]) {
	const o = originVm;
	if (!o) throw new Error('No document is open.');
	const b = o.blocks;
	return {
		id: analysisClient.id(),
		docHash: o.meta.docHash,
		original: o.text,
		layout: {
			count: b.count,
			kind: b.kind,
			level: b.level,
			charStart: b.charStart,
			charEnd: b.charEnd,
			para: b.para,
		},
		text,
		edits,
	};
}

/**
 * `text` (a version of the open document) as Markdown (UD-30), made in the analysis worker: from the stored upload when it
 * was Markdown, else from the document model's structure. The operator's edits of that version's paragraphs (UD-31) are used.
 */
export async function markdownOf(text: string): Promise<{
	markdown: string;
	changed: number;
	fromSource: boolean;
	dropped: { para: number; text: string }[];
	moved: number;
}> {
	const r = await analysisClient.request(
		{ type: 'versionMarkdown', ...markdownRequest(text, editsOfText(text)) },
		['markdown']
	);
	setMarkupHint(text, r.moved, r.dropped.length);
	return {
		markdown: r.markdown,
		changed: r.changed,
		fromSource: r.fromSource,
		dropped: r.dropped,
		moved: r.moved,
	};
}

/** The rewritten paragraphs of version `n` for the review of its inline Markdown (UD-31), with the edits stored for it. */
export async function reviewMarkdown(
	n: number
): Promise<{ items: ReviewItem[]; moved: number; dropped: number } | null> {
	const text = versionText(n);
	if (text === null || n < 1) return null;
	const r = await analysisClient.request(
		{ type: 'markdownReview', ...markdownRequest(text, editsOfText(text)) },
		['review']
	);
	setMarkupHint(text, r.moved, r.dropped);
	return { items: r.items, moved: r.moved, dropped: r.dropped };
}

/** Stores the operator's Markdown edits with version `n`; every later download of it uses them (and a reload keeps them). */
export async function saveMarkdownEdits(n: number, edits: readonly MarkdownEdit[]): Promise<void> {
	const c = chain.peek();
	if (!c || !c.versions[n - 1]) return;
	await save(setMarkdownEdits(c, n, edits));
}

/** Shows the latest version. */
export const showLatest = (): Promise<void> => showVersion(chain.peek()?.versions.length ?? 0);

/**
 * "Re-analyse the latest version" (UD-30): a new full analysis of the latest version's text, whatever analysis is kept, and
 * every view on it (with the work indicator). A pending re-measure after reverts is this one. The next action works on the
 * same text and plans its targets from its own analysis of it. Resolves true when the views show it.
 */
export async function reanalyseLatest(): Promise<boolean> {
	await loading;
	const c = chain.peek();
	if (!c || askPending()) return false;
	if (reviseTimer !== null) {
		clearTimeout(reviseTimer);
		reviseTimer = null;
	}
	latestVm = null;
	await showVersion(c.versions.length);
	return latestOnScreen();
}

/**
 * A step finished on `base`: it becomes the next version when `base` is still the latest (an unfinished step is not a
 * version, and one that finished after another step was added is left out). The views switch to the new version.
 */
export async function stepFinished(step: {
	text: string;
	made: VersionMaker;
	base: VersionRef;
}): Promise<boolean> {
	await loading;
	const c = chain.peek();
	if (!c || !canAppend(c, step.base)) return false;
	await save(append(c, { text: step.text, made: step.made }));
	void showLatest();
	return true;
}

/** "Undo last step": the latest version goes; its job stays in the list, marked undone. */
export async function undoLastStep(): Promise<void> {
	const c = chain.peek();
	if (!c || c.versions.length === 0) return;
	latestVm = null;
	await save(undo(c));
	await showLatest();
}

/** "Start again from the original": every version goes; the jobs stay in the list. */
export async function startAgain(): Promise<void> {
	const c = chain.peek();
	if (!c) return;
	latestVm = null;
	const fresh = { ...emptyChain(c.root), ...(c.origin ? { origin: c.origin } : {}) };
	chain.value = fresh;
	rewrittenText.value = null;
	if (fresh.origin) await putChain(fresh);
	else await removeChain(c.root);
	await showVersion(0);
}

/** "Continue from this result": a finished job outside the chain whose base is the latest becomes the next version. */
export async function adoptJob(job: BenchJob): Promise<boolean> {
	await loading;
	const c = chain.peek();
	if (!c || askPending() || !canAdopt(c, job)) return false;
	await save(adopt(c, job));
	void showLatest();
	return true;
}

/**
 * A revert in the latest step: the latest version's text becomes `text` and the units are recorded. The views re-measure it
 * once the reverts pause for 1.5 s (a run of reverts is one analysis).
 */
export async function reviseLatestStep(text: string, reverted: readonly string[]): Promise<void> {
	const c = chain.peek();
	if (!c || askPending() || c.versions.length === 0) return;
	latestVm = null;
	await save(reviseLatest(c, text, reverted));
	if (reviseTimer !== null) clearTimeout(reviseTimer);
	reviseTimer = setTimeout(() => {
		reviseTimer = null;
		if (viewing.peek() === (chain.peek()?.versions.length ?? 0)) void showLatest();
	}, 1500);
}

/** Version 0's plain-text summary is measured once and kept in the chain (every overview's "before", P3). */
export async function keepOrigin(root: string, summary: DocChain['origin']): Promise<void> {
	const c = chain.peek();
	if (!c || c.root !== root || !summary || c.origin) return;
	await save({ ...c, origin: summary });
}

/** Whether the saved versions of the open document wait for the customer's answer (UD-28): the bench actions are refused. */
export const askPending = (): boolean => versionsAsk.peek() !== null;

/** A rewrite is running on the open document (here or in another tab): its result would land in the chain, so the versions stay. */
export function documentBusy(root: string): boolean {
	const live = liveJobId.value;
	return jobs.value.some((j) => j.docHash === root && (j.status === 'running' || j.id === live));
}

/** "Restore the N versions": the views go to the latest version, as a reload does. */
export async function restoreVersions(): Promise<void> {
	const c = chain.peek();
	if (!c || versionsAsk.peek() !== c.root) return;
	versionsAsk.value = null;
	versionsAskOpen.value = false;
	rewrittenText.value = c.versions.at(-1)?.text ?? null;
	addNotice(VERSIONS.restored(String(c.versions.length)));
	await showLatest();
}

/** "Start from the original": every version goes, as Start again does; the jobs stay in the list. */
export async function startFromOriginal(): Promise<void> {
	const c = chain.peek();
	if (!c || versionsAsk.peek() !== c.root || documentBusy(c.root)) return;
	versionsAsk.value = null;
	versionsAskOpen.value = false;
	await startAgain();
}

/** Escape or Close: the versions stay stored and the original stays on screen; the question stays until it is answered. */
export function dismissAsk(): void {
	versionsAskOpen.value = false;
}

/** The question is shown again (the bar's "Choose" button). */
export function reopenAsk(): void {
	if (versionsAsk.peek() !== null) versionsAskOpen.value = true;
}

/**
 * The open document changed: its chain is loaded (or started). A document with saved versions asks before restoring them
 * (UD-28); the boot's own restore (a reload) goes straight to the latest version.
 */
async function opened(vm: ViewModel): Promise<void> {
	const root = vm.meta.docHash;
	const same = chain.peek()?.root === root;
	originVm = vm;
	shownHash = root;
	versionsAsk.value = null;
	versionsAskOpen.value = false;
	if (!same) {
		latestVm = null;
		chain.value = null;
		rewrittenText.value = null;
		viewing.value = 0;
	}
	const stored = same ? chain.peek() : ((await getChain(root)) ?? emptyChain(root));
	if (originVm !== vm || !stored) return;
	chain.value = stored;
	if (stored.versions.length === 0) {
		rewrittenText.value = null;
		viewing.value = 0;
		return;
	}
	if (!isBootRestored(vm)) {
		// the original is on screen and nothing uses the versions until the customer answers
		rewrittenText.value = null;
		viewing.value = 0;
		versionsAsk.value = root;
		versionsAskOpen.value = true;
		return;
	}
	rewrittenText.value = stored.versions.at(-1)?.text ?? null;
	if (!same) addNotice(VERSIONS.restored(String(stored.versions.length)));
	await showLatest();
}

export function bootChain(): void {
	effect(() => {
		const vm = result.value;
		if (!vm) {
			// a new analysis is starting: whatever was being measured belongs to the document that is going
			if (measuring) stopMeasuring();
			versionsAsk.value = null;
			versionsAskOpen.value = false;
			return;
		}
		// a version on screen (or the original put back by the bar) is not a newly opened document
		if (vm.meta.version || vm === originVm) return;
		loading = opened(vm);
	});
}
